import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { dispatch as busDispatch } from 'use-bus';
import { api } from 'api/http';
import { CompanyContext } from './CompanyContext';

const TERMINAL_STATUSES = ['ready', 'failed', 'timeout'];
const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 5000;
const POLL_FAST_WINDOW_MS = 30 * 1000;
// Once every open row is past its expected time, what is left waits on downloads the API is
// still retrying, possibly for hours: no need to ask every few seconds.
const POLL_WAITING_MS = 30 * 1000;
// Safety net for queue-worker outages. Normal failure path is the backend's
// FetchExternalMediaJob::failed() hook writing media_fetch_failures, which
// the next polling tick picks up — typically within ~1 min of submit.
const ROW_TIMEOUT_MS = 3 * 60 * 1000;
const AUTO_DISMISS_MS = 3000;
const CONTENT_PREVIEW_MAX = 80;
export const BULK_PROGRESS_READY_EVENT = 'bulkProgressPostsReady';

const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|avif|bmp|svg|heic|heif)(\?|$)/i.test(url || '');
const truncate = (text, max = CONTENT_PREVIEW_MAX) => {
    if (!text) return '';
    const trimmed = text.trim();
    return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
};

const pickThumbnailFromGroup = (group) => {
    const posts = group?.posts?.data || group?.posts || [];
    for (const post of posts) {
        const assets = post?.assets?.data || post?.assets || [];
        for (const asset of assets) {
            const image = asset?.image?.data || asset?.image;
            const imageThumb = image?.thumbnail?.data || image?.thumbnail;
            if (imageThumb?.url) return imageThumb.url;
            if (image?.url) return image.url;
            const video = asset?.video?.data || asset?.video;
            const videoThumb = video?.thumbnail?.data || video?.thumbnail;
            if (videoThumb?.url) return videoThumb.url;
        }
    }
    return null;
};

const getPostFailedUrls = (post) => {
    let raw = post?.status_message;
    if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch (_e) { raw = null; }
    }
    const arr = raw?.media_fetch_failures;
    return Array.isArray(arr) ? arr : [];
};

// The API's download bookkeeping for a bulk-imported post (MediaFetchGate): how many links
// are still to come, how many it gave up on, and until when it holds the post back for them.
// Without a deadline (an older post, or an API that does not send it) the row counts as before.
const getPostFetchState = (post, now) => {
    // The API sends microseconds (.123456Z): keep milliseconds, which every browser parses.
    const until = post?.media_fetch_until
        ? Date.parse(String(post.media_fetch_until).replace(/(\.\d{3})\d+/, '$1'))
        : NaN;
    const tracked = Number.isFinite(until);
    const pending = Math.max(0, Number(post?.media_fetch_pending) || 0);
    return {
        tracked,
        failed: Math.max(0, Number(post?.media_fetch_failed) || 0),
        waiting: tracked && pending > 0 && now < until,
    };
};

// For each post we treat assets + permanently-failed URLs as "resolved".
// A group is fully resolved when every post has resolved every expected URL.
const summarizeGroup = (group, expected = 0, now = Date.now()) => {
    const posts = group?.posts?.data || group?.posts || [];
    if (posts.length === 0) {
        return { minResolved: 0, minAssets: 0, maxFailed: 0, failedUrls: [], errorMessage: null, waiting: false };
    }
    let minResolved = Infinity;
    let minAssets = Infinity;
    let maxFailed = 0;
    let errorMessage = null;
    let waiting = false;
    const failedSet = new Set();
    for (const post of posts) {
        const assets = post?.assets?.data || post?.assets || [];
        const failed = getPostFailedUrls(post);
        failed.forEach((u) => failedSet.add(u));
        const fetchState = getPostFetchState(post, now);
        if (fetchState.waiting) waiting = true;
        // When none of the media of a post that needs it (Instagram, TikTok, Pinterest,
        // YouTube) could be downloaded, the API moves it to error and stores its own
        // sentence instead of the media_fetch_failures list: that post is done, and failed.
        const erroredWithoutMedia = post?.status === 'error' && assets.length === 0;
        // Every download of the post ended, or its deadline passed: a link that is not
        // attached by now is not coming.
        const settled = fetchState.tracked && !fetchState.waiting;
        let failedCount = Math.max(failed.length, fetchState.failed);
        if (erroredWithoutMedia || settled) failedCount = Math.max(failedCount, expected - assets.length);
        const resolved = assets.length + failedCount;
        if (erroredWithoutMedia && !errorMessage && typeof post?.status_message === 'string') {
            errorMessage = post.status_message;
        }
        if (resolved < minResolved) minResolved = resolved;
        if (assets.length < minAssets) minAssets = assets.length;
        if (failedCount > maxFailed) maxFailed = failedCount;
    }
    return {
        minResolved: minResolved === Infinity ? 0 : minResolved,
        minAssets: minAssets === Infinity ? 0 : minAssets,
        maxFailed,
        failedUrls: Array.from(failedSet),
        errorMessage,
        waiting,
    };
};

const rowTimeoutMs = (row) => ROW_TIMEOUT_MS + (row.mediaEtaMs || 0);

// Same object when nothing changed, so a poll that brings no news does not re-render.
const asDownloading = (row, failedCount) =>
    row.status === 'downloading' && row.failedCount === failedCount
        ? row
        : { ...row, status: 'downloading', failedCount };

const BulkProgressContext = createContext({
    isOpened: false,
    isCollapsed: false,
    rows: [],
    trackBulkResults: (_response, _rowsForMode, _networks) => undefined,
    closeBulkProgress: () => undefined,
    toggleCollapse: () => undefined,
});

const BulkProgressProvider = ({ children }) => {
    const { company } = useContext(CompanyContext);

    const [rows, setRows] = useState([]);
    const [isOpened, setIsOpened] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [lastTrackedAt, setLastTrackedAt] = useState(0);

    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const dismissTimerRef = useRef(null);
    const inFlightRef = useRef(false);
    const lastPollAtRef = useRef(0);

    const close = useCallback(() => {
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
        setIsOpened(false);
        setIsCollapsed(false);
        setRows([]);
    }, []);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((c) => !c);
    }, []);

    const trackBulkResults = useCallback((response, rowsForMode, networks = []) => {
        const results = response?.results || [];
        const now = Date.now();
        const incoming = [];
        const sharedNetworks = Array.isArray(networks) ? networks : [];

        results.forEach((result, idx) => {
            const rowForMode = rowsForMode?.[result?.row ?? idx];
            const content = truncate(rowForMode?.content || '');
            const mediaUrls = Array.isArray(rowForMode?.media_urls) ? rowForMode.media_urls : [];
            const firstImage = mediaUrls.find(isImageUrl) || null;

            if (result?.status === 'ok' && result?.post_group_id) {
                incoming.push({
                    key: `pg-${result.post_group_id}`,
                    post_group_id: result.post_group_id,
                    content,
                    media_urls: mediaUrls,
                    networks: sharedNetworks,
                    thumbnail: firstImage,
                    status: mediaUrls.length === 0 ? 'ready' : 'queued',
                    errorMessage: null,
                    startedAt: now,
                    // The API paces downloads per site; it reports when this row's last
                    // download is queued to start, so the timeout waits at least that long.
                    mediaEtaMs: (Number(result?.media_eta_seconds) || 0) * 1000,
                });
            } else {
                incoming.push({
                    key: `err-${now}-${idx}`,
                    post_group_id: null,
                    content,
                    media_urls: mediaUrls,
                    networks: sharedNetworks,
                    thumbnail: firstImage,
                    status: 'failed',
                    errorMessage: result?.message || 'Failed to create post.',
                    startedAt: now,
                });
            }
        });

        if (incoming.length === 0) return;

        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }

        setRows((prev) => {
            const existingIds = new Set(prev.map((r) => r.key));
            const deduped = incoming.filter((r) => !existingIds.has(r.key));
            return [...prev, ...deduped];
        });
        setLastTrackedAt(now);
        setIsOpened(true);
        setIsCollapsed(false);
    }, []);

    const activeIds = useMemo(
        () =>
            rows
                .filter((r) => !TERMINAL_STATUSES.includes(r.status) && r.post_group_id)
                .map((r) => r.post_group_id),
        [rows]
    );

    const pollDelay = useMemo(() => {
        if (!company?.id || activeIds.length === 0) return null;
        const elapsed = Date.now() - lastTrackedAt;
        return elapsed < POLL_FAST_WINDOW_MS ? POLL_FAST_MS : POLL_SLOW_MS;
    }, [company?.id, activeIds.length, lastTrackedAt]);

    const reconcileRows = useCallback((groupsById) => {
        const now = Date.now();
        const newlyReadyIds = [];

        setRows((prev) =>
            prev.map((row) => {
                if (TERMINAL_STATUSES.includes(row.status)) return row;
                if (!row.post_group_id) return row;

                const timeoutMs = rowTimeoutMs(row);
                const group = groupsById.get(row.post_group_id);
                if (!group) {
                    if (now - row.startedAt > timeoutMs) {
                        return { ...row, status: 'timeout', errorMessage: 'Media took too long to download.' };
                    }
                    return row;
                }

                const expected = row.media_urls.length;
                const summary = summarizeGroup(group, expected, now);
                const failedCount = Math.min(summary.maxFailed, expected);

                // The API still holds a post back for its downloads (paced per site, or
                // retrying): the row waits as long as it does, past the usual timeout.
                if (summary.waiting) {
                    return asDownloading(row, failedCount);
                }

                const isResolved = expected === 0 || summary.minResolved >= expected;

                if (isResolved) {
                    const allFailed = expected > 0 && summary.minAssets === 0 && summary.maxFailed >= expected;
                    if (allFailed) {
                        return {
                            ...row,
                            status: 'failed',
                            failedCount,
                            errorMessage: summary.failedUrls.length
                                ? `Couldn't download: ${summary.failedUrls.slice(0, 3).join(', ')}${summary.failedUrls.length > 3 ? '…' : ''}`
                                : summary.errorMessage || "Couldn't download media for this row.",
                        };
                    }
                    newlyReadyIds.push(row.post_group_id);
                    const thumb = pickThumbnailFromGroup(group) || row.thumbnail;
                    return {
                        ...row,
                        status: 'ready',
                        thumbnail: thumb,
                        failedCount,
                        errorMessage: failedCount > 0
                            ? `${failedCount} of ${expected} media file${expected === 1 ? '' : 's'} failed to download.`
                            : null,
                    };
                }

                if (now - row.startedAt > timeoutMs) {
                    return { ...row, status: 'timeout', errorMessage: 'Media took too long to download.' };
                }

                return asDownloading(row, failedCount);
            })
        );

        if (newlyReadyIds.length > 0) {
            busDispatch({
                type: BULK_PROGRESS_READY_EVENT,
                payload: { postGroupIds: newlyReadyIds },
            });
        }
    }, []);

    useEffect(() => {
        if (pollDelay === null) return undefined;

        let cancelled = false;

        const tick = async () => {
            if (cancelled || inFlightRef.current) return;
            const active = rowsRef.current.filter((r) => !TERMINAL_STATUSES.includes(r.status) && r.post_group_id);
            const ids = active.map((r) => r.post_group_id);
            if (ids.length === 0 || !company?.id) return;

            const now = Date.now();
            const allOverdue = active.every((r) => now - r.startedAt > rowTimeoutMs(r));
            if (allOverdue && now - lastPollAtRef.current < POLL_WAITING_MS) return;
            lastPollAtRef.current = now;

            inFlightRef.current = true;
            try {
                const response = await api.get(`/companies/${company.id}/postGroups`, {
                    'filter[id]': ids.join(','),
                    include: 'posts.assets.image.thumbnail,posts.assets.video.thumbnail',
                    paginate: false,
                });
                const groups = response?.data || [];
                const map = new Map();
                groups.forEach((g) => {
                    if (g?.id != null) map.set(g.id, g);
                });
                if (!cancelled) reconcileRows(map);
            } catch (_e) {
                // swallow; next tick retries
            } finally {
                inFlightRef.current = false;
            }
        };

        const id = setInterval(tick, pollDelay);
        tick();

        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [pollDelay, company?.id, reconcileRows]);

    useEffect(() => {
        if (rows.length === 0) return undefined;
        const allTerminal = rows.every((r) => TERMINAL_STATUSES.includes(r.status));
        if (!allTerminal) {
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
            return undefined;
        }
        dismissTimerRef.current = setTimeout(() => {
            dismissTimerRef.current = null;
            setIsOpened(false);
            setRows([]);
        }, AUTO_DISMISS_MS);
        return () => {
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
        };
    }, [rows]);

    const value = useMemo(
        () => ({
            isOpened,
            isCollapsed,
            rows,
            trackBulkResults,
            closeBulkProgress: close,
            toggleCollapse,
        }),
        [isOpened, isCollapsed, rows, trackBulkResults, close, toggleCollapse]
    );

    return <BulkProgressContext.Provider value={value}>{children}</BulkProgressContext.Provider>;
};

export { BulkProgressContext, BulkProgressProvider };
